import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Card, Form, ListGroup } from 'react-bootstrap';

// Reusable, chapter-configured Zelle payment option. It fetches the current
// chapter's Zelle receiving config (public endpoint, mirrors the paypal one)
// and renders NOTHING when the chapter has not enabled Zelle — so every
// checkout surface that drops this in shows exactly PayPal-only behavior until
// an admin opts in. On submit it calls onSubmit with the same shape the 2024
// convention already uses: { paymentType: 'zelle', confirmationNumber }.
//
// The payment is recorded by the backend as pending/unpaid; an admin confirms
// it later through the existing manual confirmation flow. There is no Zelle
// verification API, so the payer-entered reference is stored for admins to
// match against bank deposits.
const ChapterZellePayment = ({ amount, onSubmit }) => {
  const [config, setConfig] = useState({ zelleEnabled: false });
  const [showForm, setShowForm] = useState(false);
  const [reference, setReference] = useState('');

  const checkChapter = window.location.host;

  useEffect(() => {
    let active = true;
    const fetchConfig = async () => {
      try {
        const { data } = await axios.get(
          `/api/chapters/zelle/${checkChapter}`
        );
        if (active) {
          setConfig(data || { zelleEnabled: false });
        }
      } catch (error) {
        if (active) {
          setConfig({ zelleEnabled: false });
        }
      }
    };
    fetchConfig();
    return () => {
      active = false;
    };
  }, [checkChapter]);

  if (!config.zelleEnabled) {
    return null;
  }

  // `amount` is carried in the payload so the backend records exactly the
  // figure the payer was shown. Zelle has no capture API to report an amount,
  // and the surfaces compute their totals differently, so the displayed amount
  // is the single source of truth.
  const handleSubmit = () => {
    onSubmit({
      paymentType: 'zelle',
      confirmationNumber: reference.trim(),
      amount: Number(amount || 0),
    });
  };

  const amountLabel =
    amount || amount === 0 ? `$${Number(amount).toFixed(2)}` : '';

  return (
    <div className='mt-2'>
      {/* Same variant/weight as the other primary actions on these pages
          (Register / Submit use variant="info"), so the option is plainly
          visible next to the PayPal button. `block` keeps it full-width and
          tappable at mobile widths. */}
      <Button block variant='info' onClick={() => setShowForm(!showForm)}>
        Pay with Zelle
      </Button>

      {showForm ? (
        <Card border='info' className='mt-2'>
          <Card.Body>
            <ListGroup variant='flush' className='mb-2'>
              <ListGroup.Item>
                Zelle recipient: <b>{config.zelleRecipientName}</b>
              </ListGroup.Item>
              <ListGroup.Item>
                Zelle recipient ID: <b>{config.zelleRecipientId}</b>
              </ListGroup.Item>
              {amountLabel ? (
                <ListGroup.Item>
                  Amount: <b>{amountLabel}</b>
                </ListGroup.Item>
              ) : null}
            </ListGroup>

            <p className='mb-2'>
              Send the amount above by Zelle, then enter your Zelle confirmation
              number or the sender name and submit.
            </p>

            <Form.Group controlId='zelleReference'>
              <Form.Control
                type='text'
                placeholder='Zelle confirmation number or sender name'
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <p className='text-muted small mb-2'>
              Your payment will be pending until an admin verifies it.
            </p>

            <Button
              block
              variant='info'
              onClick={handleSubmit}
              disabled={reference.trim() === ''}
            >
              Submit
            </Button>
          </Card.Body>
        </Card>
      ) : null}
    </div>
  );
};

export default ChapterZellePayment;
